import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import type { ReactRef } from './types';

// Utils for Popper and Dropdown components

export function setRef(ref, value) {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref) {
    ref.current = value;
  }
}

export function useForkRef(refA, refB) {
  return useMemo(() => {
    if (refA == null && refB == null) {
      return null;
    }
    return (refValue) => {
      setRef(refA, refValue);
      setRef(refB, refValue);
    };
  }, [refA, refB]);
}

export function createChainedFunction(...funcs) {
  return funcs.reduce(
    (acc, func) => {
      if (func == null) {
        return acc;
      }

      return function chainedFunction(...args) {
        // tslint:disable-next-line:no-invalid-this
        acc.apply(this, args);
        // tslint:disable-next-line:no-invalid-this
        func.apply(this, args);
      };
    },
    () => {}
  );
}

// tslint:disable-next-line:no-typeof-undefined
export const useEnhancedEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export function usePrevious<T>(value: T): T {
  const ref: any = useRef<T>();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

export const wrapEvent = (theirHandler, ourHandler) => (event) => {
  if (theirHandler) {
    theirHandler(event);
  }

  if (!event.defaultPrevented) {
    return ourHandler(event);
  }
};

export function assignRef<T = any>(ref: ReactRef<T> | undefined, value: T) {
  if (ref == null) return;

  try {
    (ref as any).current = value;
  } catch (error) {
    throw new Error(`Cannot assign value '${value}' to ref '${ref}'`);
  }
}

export function mergeRefs<T>(...refs: (ReactRef<T> | undefined)[]) {
  return (value: T) => {
    refs.forEach((ref) => assignRef(ref, value));
  };
}

export interface ITestProps {
  /** Value for `data-test` attribute, used for testing purposes only */
  dataTest?: string;
}

export { matchDisplayName } from './matchDisplayName';
export { dataTest } from './dataTest';

export type { IBaseProps } from './baseInterface';
export { componentFactory } from './componentFactory';

export { StateWrapper } from './stories/StateWrapper';
